import type { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import * as structureDefinitions from '@seen/core/structureDefinitions'
import { Separator } from '@seen/ui'
import { compact } from 'lodash-es'
import { type JSX, useContext, useState } from 'react'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { NumberInput } from '#src/components/core/NumberInput.tsx'
import { Switch } from '#src/components/core/Switch.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolRadioGroupField } from '#src/components/validated-tool-form/shared/ValidatedToolRadioGroupField.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import { CurrentParticipantContext } from '#src/contexts/index.ts'

export interface HomeCareUtilizationFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* Per-field AI suggestion overlay, keyed by linkId. */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
}

const dependencyLevel = z
  .enum([
    'independent',
    'minimally-dependent',
    'moderately-dependent',
    'dependent',
  ])
  .nullable()
  .refine((v) => v !== null, { message: 'Please select an option' })

const schema = z.object({
  bathing: dependencyLevel,
  'bathing-per-week': z.number().nullable(),
  'bathing-per-day': z.number().nullable(),
  'dressing-grooming': dependencyLevel,
  'dressing-grooming-per-week': z.number().nullable(),
  'dressing-grooming-per-day': z.number().nullable(),
  toileting: dependencyLevel,
  'toileting-per-week': z.number().nullable(),
  'toileting-per-day': z.number().nullable(),
  'ambulation-transfers': dependencyLevel,
  'ambulation-transfers-per-week': z.number().nullable(),
  'ambulation-transfers-per-day': z.number().nullable(),
  eating: dependencyLevel,
  'eating-per-week': z.number().nullable(),
  'eating-per-day': z.number().nullable(),
  'meal-preparation': dependencyLevel,
  'meal-preparation-per-week': z.number().nullable(),
  'meal-preparation-per-day': z.number().nullable(),
  'medication-reminders': dependencyLevel,
  'medication-reminders-per-week': z.number().nullable(),
  'medication-reminders-per-day': z.number().nullable(),
  laundry: dependencyLevel,
  'laundry-per-week': z.number().nullable(),
  'light-housekeeping': dependencyLevel,
  'light-housekeeping-per-week': z.number().nullable(),
  'grocery-shopping-errands': dependencyLevel,
  'grocery-shopping-errands-per-week': z.number().nullable(),
  'other-domestic-services-notes': z.string().nullable(),
  'other-domestic-services-hours': z.number().min(0).step(0.05).nullable(),
  summary: z.string().nullable(),
})

const validatedTool = new ValidatedTool(
  questionnaires.homeCareUtilizationQuestionnaire
)

export function HomeCareUtilizationForm({
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  restorationId,
  ...delegated
}: HomeCareUtilizationFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })
  const fieldSuggestions = renderFieldSuggestions?.(form.setSuggestionValue)
  const [isPrintView, setIsPrintView] = useState(false)
  const participant = useContext(CurrentParticipantContext)

  const getOptionLabel = (label: string, minutes: string): string => {
    if (isPrintView) {
      return label
    }
    return `${label} (${minutes})`
  }

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle="For determining the estimated number of weekly home care hours"
      form={form}
      response={response}
      {...delegated}
    >
      <div className="mb-4 flex items-center gap-2">
        <Switch
          id="print-view"
          checked={isPrintView}
          onCheckedChange={setIsPrintView}
        />
        <FormLabel htmlFor="print-view">Print View</FormLabel>
      </div>

      {isPrintView && (
        <div className="mb-6">
          <div className="mb-3">
            <div className="text-lg font-semibold">{participant?.name}</div>
            {participant?.seenMRN && (
              <div className="text-sm text-foreground-secondary">
                MRN {participant.seenMRN}
              </div>
            )}
          </div>
          <Separator />
        </div>
      )}

      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score
            form={form}
            validatedTool={validatedTool}
            response={response}
          />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add additional notes…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}
      <ValidatedToolSection title="Light Housekeeping">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="light-housekeeping"
          suggestion={fieldSuggestions?.['light-housekeeping']}
          options={[
            {
              value: 'independent',
              label: 'Independent',
            },
            {
              value: 'minimally-dependent',
              label: getOptionLabel('Standby assistance', '60 min'),
            },
            {
              value: 'moderately-dependent',
              label: getOptionLabel('Moderate assistance', '60 min'),
            },
            {
              value: 'dependent',
              label: getOptionLabel('Dependent', '60 min'),
            },
          ]}
        />
        <FormField
          control={form.control}
          name="light-housekeeping-per-week"
          render={({ field }) => (
            <>
              <FormLabel>Days per week</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Meal Preparation">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="meal-preparation"
          suggestion={fieldSuggestions?.['meal-preparation']}
          options={[
            {
              value: 'independent',
              label: 'Independent',
            },
            {
              value: 'minimally-dependent',
              label: getOptionLabel('Standby assistance', '30 min'),
            },
            {
              value: 'moderately-dependent',
              label: getOptionLabel('Moderate assistance', '30 min'),
            },
            {
              value: 'dependent',
              label: getOptionLabel('Dependent', '30 min'),
            },
          ]}
        />
        <FormField
          control={form.control}
          name="meal-preparation-per-day"
          render={({ field }) => (
            <>
              <FormLabel>Times per day</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
        <FormField
          control={form.control}
          name="meal-preparation-per-week"
          render={({ field }) => (
            <>
              <FormLabel>Days per week</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Grocery Shopping">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="grocery-shopping-errands"
          suggestion={fieldSuggestions?.['grocery-shopping-errands']}
          options={[
            {
              value: 'independent',
              label: 'Independent',
            },
            {
              value: 'minimally-dependent',
              label: getOptionLabel('Standby assistance', '60 min'),
            },
            {
              value: 'moderately-dependent',
              label: getOptionLabel('Moderate assistance', '60 min'),
            },
            {
              value: 'dependent',
              label: getOptionLabel('Dependent', '60 min'),
            },
          ]}
        />
        <FormField
          control={form.control}
          name="grocery-shopping-errands-per-week"
          render={({ field }) => (
            <>
              <FormLabel>Days per week</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Laundry">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="laundry"
          suggestion={fieldSuggestions?.laundry}
          options={[
            {
              value: 'independent',
              label: 'Independent',
            },
            {
              value: 'minimally-dependent',
              label: getOptionLabel('Standby assistance', '60 min'),
            },
            {
              value: 'moderately-dependent',
              label: getOptionLabel('Moderate assistance', '60 min'),
            },
            {
              value: 'dependent',
              label: getOptionLabel('Dependent', '60 min'),
            },
          ]}
        />
        <FormField
          control={form.control}
          name="laundry-per-week"
          render={({ field }) => (
            <>
              <FormLabel>Days per week</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Toileting and Continence">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="toileting"
          suggestion={fieldSuggestions?.toileting}
          options={[
            {
              value: 'independent',
              label: 'Independent',
            },
            {
              value: 'minimally-dependent',
              label: getOptionLabel('Standby assistance', '15 min'),
            },
            {
              value: 'moderately-dependent',
              label: getOptionLabel('Moderate assistance', '15 min'),
            },
            {
              value: 'dependent',
              label: getOptionLabel('Dependent', '15 min'),
            },
          ]}
        />
        <FormField
          control={form.control}
          name="toileting-per-day"
          render={({ field }) => (
            <>
              <FormLabel>Times per day</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
        <FormField
          control={form.control}
          name="toileting-per-week"
          render={({ field }) => (
            <>
              <FormLabel>Days per week</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Feeding">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="eating"
          suggestion={fieldSuggestions?.eating}
          options={[
            {
              value: 'independent',
              label: 'Independent',
            },
            {
              value: 'minimally-dependent',
              label: getOptionLabel('Standby assistance', '30 min'),
            },
            {
              value: 'moderately-dependent',
              label: getOptionLabel('Moderate assistance', '30 min'),
            },
            {
              value: 'dependent',
              label: getOptionLabel('Dependent', '30 min'),
            },
          ]}
        />
        <FormField
          control={form.control}
          name="eating-per-day"
          render={({ field }) => (
            <>
              <FormLabel>Times per day</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
        <FormField
          control={form.control}
          name="eating-per-week"
          render={({ field }) => (
            <>
              <FormLabel>Days per week</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Dressing">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="dressing-grooming"
          suggestion={fieldSuggestions?.['dressing-grooming']}
          options={[
            {
              value: 'independent',
              label: 'Independent',
            },
            {
              value: 'minimally-dependent',
              label: getOptionLabel('Standby assistance', '30 min'),
            },
            {
              value: 'moderately-dependent',
              label: getOptionLabel('Moderate assistance', '30 min'),
            },
            {
              value: 'dependent',
              label: getOptionLabel('Dependent', '30 min'),
            },
          ]}
        />
        <FormField
          control={form.control}
          name="dressing-grooming-per-day"
          render={({ field }) => (
            <>
              <FormLabel>Times per day</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
        <FormField
          control={form.control}
          name="dressing-grooming-per-week"
          render={({ field }) => (
            <>
              <FormLabel>Days per week</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Ambulation & Transfers">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="ambulation-transfers"
          suggestion={fieldSuggestions?.['ambulation-transfers']}
          options={[
            {
              value: 'independent',
              label: 'Independent',
            },
            {
              value: 'minimally-dependent',
              label: getOptionLabel('Standby assistance', '15 min'),
            },
            {
              value: 'moderately-dependent',
              label: getOptionLabel('Moderate assistance', '15 min'),
            },
            {
              value: 'dependent',
              label: getOptionLabel('Dependent', '15 min'),
            },
          ]}
        />
        <FormField
          control={form.control}
          name="ambulation-transfers-per-day"
          render={({ field }) => (
            <>
              <FormLabel>Times per day</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
        <FormField
          control={form.control}
          name="ambulation-transfers-per-week"
          render={({ field }) => (
            <>
              <FormLabel>Days per week</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Bathing, Oral Hygiene & Grooming">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="bathing"
          suggestion={fieldSuggestions?.bathing}
          options={[
            {
              value: 'independent',
              label: 'Independent',
            },
            {
              value: 'minimally-dependent',
              label: getOptionLabel('Standby assistance', '45 min'),
            },
            {
              value: 'moderately-dependent',
              label: getOptionLabel('Moderate assistance', '45 min'),
            },
            {
              value: 'dependent',
              label: getOptionLabel('Dependent', '45 min'),
            },
          ]}
        />
        <FormField
          control={form.control}
          name="bathing-per-day"
          render={({ field }) => (
            <>
              <FormLabel>Times per day</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
        <FormField
          control={form.control}
          name="bathing-per-week"
          render={({ field }) => (
            <>
              <FormLabel>Days per week</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Assistance with Prosthetics & Medications">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="medication-reminders"
          suggestion={fieldSuggestions?.['medication-reminders']}
          options={[
            {
              value: 'independent',
              label: 'Independent',
            },
            {
              value: 'minimally-dependent',
              label: getOptionLabel('Standby assistance', '15 min'),
            },
            {
              value: 'moderately-dependent',
              label: getOptionLabel('Moderate assistance', '15 min'),
            },
            {
              value: 'dependent',
              label: getOptionLabel('Dependent', '15 min'),
            },
          ]}
        />
        <FormField
          control={form.control}
          name="medication-reminders-per-day"
          render={({ field }) => (
            <>
              <FormLabel>Times per day</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
        <FormField
          control={form.control}
          name="medication-reminders-per-week"
          render={({ field }) => (
            <>
              <FormLabel>Days per week</FormLabel>
              <NumberInput {...field} className="max-w-24" />
            </>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Other Domestic Services">
        <FormField
          control={form.control}
          name="other-domestic-services-notes"
          render={({ field }) => (
            <>
              <FormLabel>Notes</FormLabel>
              <RichTextEditor
                defaultValue={field.value ?? ''}
                placeholder="Add notes..."
                contentClassName="max-w-(--container-prose)"
                {...field}
              />
            </>
          )}
        />
        <FormField
          control={form.control}
          name="other-domestic-services-hours"
          render={({ field }) => (
            <>
              <FormLabel>Hours per week</FormLabel>
              <NumberInput
                {...field}
                className="max-w-24"
                step={0.05}
                min={0}
              />
            </>
          )}
        />
      </ValidatedToolSection>
      <Score form={form} validatedTool={validatedTool} response={response} />
      <ValidatedToolSection title="Additional notes">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add notes…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({
  form,
  validatedTool,
  response,
}: {
  form: any // TODO: fix type
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })
  const score = result.flatItems.reduce((acc, item) => {
    const itemScore = scoreItem(item)
    const daysPerWeek =
      result.getAnswer(`${item.linkId}-per-week`)?.valueInteger ?? 0
    const timesPerDay =
      result.getAnswer(`${item.linkId}-per-day`)?.valueInteger ?? 1
    return acc + itemScore * daysPerWeek * timesPerDay
  }, 0)

  const otherServicesHours =
    result.getAnswer('other-domestic-services-hours')?.valueDecimal ?? 0
  const totalMinutes = score + otherServicesHours * 60

  return (
    <div className="flex items-center gap-2">
      <h2>
        Total time per week:{' '}
        <b>{convertMinutesToHoursAndMinutes(totalMinutes)}</b> ({totalMinutes}m)
      </h2>
    </div>
  )
}

function scoreItem(item: QuestionnaireResponseItem): number {
  const scores = compact(
    item.answer?.map((a) => {
      const ordinalValue = a.extension?.find(
        (e) => e.url === structureDefinitions.ORDINAL_VALUE_EXTENSION_URL
      )
      return ordinalValue?.valueDecimal ?? ordinalValue?.valueInteger ?? 0
    })
  )
  return scores.reduce((acc, score) => acc + score, 0)
}

function convertMinutesToHoursAndMinutes(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}h ${minutes}m`
}
